/**
 * OfficerCard — single officer slot in the personnel grid.
 *
 * Layout (top to bottom):
 *   [Role / name header]                              [✕ remove]
 *   [post icons]   [badge img + CS overlay + select]   [equipment icons]
 *   [Full name] [shift times] [Edit Equipment link]
 *
 * The center badge has a transparent circular <select> overlaid that lets
 * supervisors assign anyone from the eligible roster categories. Clicking
 * "Edit Equipment" opens the EquipmentModal for keys/radios/recorders/posts.
 */
import { useState } from 'react'
import { useDesktopStore, type PersonnelEntry } from '../../stores/desktop'
import { useAuthStore } from '../../stores/auth'
import { markDirty } from '../../stores/sync'
import { useSyncStore } from '../../stores/sync'
import { saveEquipmentAssignment, removeFromPosting } from '../../stores/postingBridge'
import { KEY_ICON, REC_ICON, WALKIE_ICON_BG, getPostIcon } from '../../assets'
import {
  getBadge,
  RANK_LABEL,
  DISPLAY_ROLE,
  stripNickname,
  csNumber,
  groupPosts,
  loadRoster,
  getRosterForRole,
  getTasksForCS,
} from './personnelHelpers'
import PostIcon from './PostIcon'
import EquipmentModal from './EquipmentModal'
import FlashSVG from '../equipment/FlashSVG'

interface OfficerCardProps {
  entry: PersonnelEntry
  index: number
  isSup: boolean
}

export default function OfficerCard({ entry, index, isSup }: OfficerCardProps) {
  const { updateOfficer, toggleFcpd, clearSlotByIdentity } = useDesktopStore()
  const [showModal, setShowModal] = useState(false)
  const accessLevel = useAuthStore(s => {
    const sess = s.session
    if (!sess) return 'officer'
    if (sess.accessLevel) return sess.accessLevel
    const r = (sess.rank || '').toLowerCase()
    if (r === 'developer' || r === 'dv') return 'developer'
    if (r.includes('director') || r.includes('assistant') || r.includes('lieut') || r.includes('lt.')) return 'exec'
    if (r.includes('serg') || r.includes('sgt') || r.includes('oic')) return 'supervisor'
    return 'officer'
  })
  const isOfficer = accessLevel === 'officer'

  const roster      = loadRoster()
  const allowedCats = getRosterForRole(entry.role)
  // FCPD detection is hardened against stale role data: any SC-* call sign is
  // FCPD regardless of what entry.role currently says. This makes the badge
  // correct on the very first render frame after toggle (no officer-badge
  // flash) and immune to a pulled personnel blob that demoted the role.
  const isFcpdEntry  = entry.role === 'FCPD' || /^SC-?\d+$/i.test(entry.callSign || '')
  const effectiveRole = isFcpdEntry ? 'FCPD' : entry.role
  const badgeImg    = getBadge(isSup, effectiveRole)
  const cs          = csNumber(entry.callSign)
  const grouped     = groupPosts(entry.posts)
  const secondaryTasks = useDesktopStore(s => s.secondaryTasks)
  const officerTasks   = getTasksForCS(secondaryTasks, entry.callSign || entry.name)

  // Badge size — FCPD gets a slightly larger badge than regular officers
  const isFcpd = isFcpdEntry
  const badgeW = isSup ? 150 : isFcpd ? 155 : 130
  const badgeH = isSup ? 204 : isFcpd ? 211 : 177

  function clearSlot() {
    // Capture identity BEFORE any state change so we clear the right person
    // even if the grid re-sorts between render and click (index can go stale
    // when the subscriber re-orders officer slots — that caused two cards to
    // appear to close at once).
    const targetCS = entry.callSign
    const targetName = entry.name
    // If this is an FCPD officer, use toggleFcpd so the button state also resets.
    // Must ALSO remove them from daily posting + markDirty, or the next
    // mergePostingToPersonnel restores them and the card resurrects.
    if (entry.role === 'FCPD' && (targetCS === 'SC-81' || targetCS === 'SC-82')) {
      if (targetCS) removeFromPosting(targetCS)
      toggleFcpd(targetCS as 'SC-81' | 'SC-82')
      // Push the removal to the cloud IMMEDIATELY. markDirty() alone only
      // schedules a push 2s later; a refresh or the 30s pull within that window
      // pulls the still-present cloud copy back and the officer resurrects on
      // desktop AND never clears on mobile. forcePush() persists it now.
      useSyncStore.getState().forcePush()
      return
    }
    // Remove from daily posting so mergePostingToPersonnel doesn't restore them on next pull
    if (targetCS) removeFromPosting(targetCS)
    // Clear by identity (callSign, falling back to name) rather than index.
    clearSlotByIdentity(targetCS, targetName)
    // Push the removal to the cloud IMMEDIATELY (see note above) so it can't be
    // reverted by a refresh/pull before the debounced push fires. This is what
    // makes the removal stick across refresh and propagate to mobile.
    useSyncStore.getState().forcePush()
  }

  function assignCS(callSign: string) {
    markDirty()
    if (!callSign) {
      clearSlot()
      return
    }
    // Find in roster
    let found: { name: string; badgeNumber?: number | null; radioNumber?: number | null } | undefined
    let foundCat = ''
    for (const [cat, entries] of Object.entries(roster)) {
      if (!allowedCats.includes(cat)) continue
      const e = entries.find(r => r.callSign === callSign)
      if (e) { found = e; foundCat = cat; break }
    }
    // If not in roster but entry already has this callSign (posting import), keep existing data
    if (!found && callSign === entry.callSign && entry.name) {
      return // already assigned, nothing to change
    }
    // Derive the correct role from roster category so supervisors in officer slots
    // display with a supervisor badge rather than the default officer badge.
    const SUP_CAT_ROLE: Record<string, string> = {
      'Director of Security': 'Dir/AD/Lt',
      'Assistant Director':   'Dir/AD/Lt',
      'Lieutenant':           'Dir/AD/Lt',
      'Developer':            'Dir/AD/Lt',
      'Sergeants':            'Sergeant',
    }
    const newRole = foundCat && SUP_CAT_ROLE[foundCat] ? SUP_CAT_ROLE[foundCat] : entry.role
    updateOfficer(index, {
      callSign,
      name:  found?.name ?? entry.name ?? '',
      badge: found?.badgeNumber ? String(found.badgeNumber) : (entry.badge || ''),
      radio: found?.radioNumber ? String(found.radioNumber) : (entry.radio || ''),
      role:  newRole,
    })
    // Persist so equipment survives posting merges
    if (callSign) {
      saveEquipmentAssignment(callSign, {
        badge: found?.badgeNumber ? String(found.badgeNumber) : (entry.badge || ''),
        radio: found?.radioNumber ? String(found.radioNumber) : (entry.radio || ''),
      })
    }
  }

  // Flatten roster for select options
  const rosterOptions: { callSign: string; name: string; cat: string }[] = []
  for (const [cat, entries] of Object.entries(roster)) {
    if (!allowedCats.includes(cat)) continue
    entries.forEach(e => {
      if (e.callSign) rosterOptions.push({ callSign: e.callSign, name: e.name, cat })
    })
  }

  // Display title — assigned person's roster category, or the slot's default role
  const assignedCat = entry.callSign
    ? rosterOptions.find(r => r.callSign === entry.callSign)?.cat ?? entry.role
    : entry.role

  const displayRole = DISPLAY_ROLE[assignedCat] ?? assignedCat

  // Card left border: blue when on post, amber when on break, purple for sups, grey when idle
  const now = new Date()
  const nowMins = now.getHours() * 60 + now.getMinutes()
  function parseMins(t: string) {
    if (!t) return null
    const s = t.replace(':', '').padStart(4, '0')
    return parseInt(s.slice(0,2)) * 60 + parseInt(s.slice(2))
  }
  function isNow(from: string, to: string) {
    const f = parseMins(from), t = parseMins(to)
    if (f === null || t === null) return true
    const end = t <= f ? t + 1440 : t
    const n = nowMins < f ? nowMins + 1440 : nowMins
    return n >= f && n < end
  }
  const currentPost = entry.posts?.find(p => !p.isBreak && p.post && isNow(p.from ?? '', p.to ?? ''))
  const currentBreak = entry.posts?.find(p => p.isBreak && isNow(p.from ?? '', p.to ?? ''))
  const cardBorderColor = isSup
    ? 'var(--purple, #9b59b6)'
    : currentBreak
    ? 'var(--amber, #e67e22)'
    : currentPost
    ? '#3b82f6'
    : entry.name
    ? 'rgba(255,255,255,0.12)'
    : 'transparent'

  return (
    <>
      {showModal && entry.name && (
        <EquipmentModal entry={entry} index={index} onClose={() => setShowModal(false)} />
      )}

      <div
        style={{
          background: 'var(--bg-elevated)',
          border: 'none',
          borderLeft: `3px solid ${cardBorderColor}`,
          borderRadius: 8,
          padding: isSup ? 12 : 8,
          // overflow: hidden removed — it was clipping the badge when the
          // grid column got narrower than the 270px flex content. The
          // grid now uses minmax() to guarantee enough width, so this is
          // a defensive removal in case anything else ever overflows.
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header: role/name + remove button */}
        <div style={{ position: 'relative', marginBottom: 4, textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
            {displayRole}
          </div>
          {entry.name && (
          <button
          onClick={clearSlot}
          title={`Remove ${entry.name}`}
          style={{
          position: 'absolute',
          top: 0,
          right: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#f87171',
          fontSize: 14,
            padding: 2,
          }}
          >
              ✕
              </button>
            )}
        </div>

        {/* Middle row: posts | badge | equipment */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          {/* LEFT: post icons — non-interactive for officers but hover tooltips still work */}
          <div
            style={{
              width: 44,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            {grouped.map((p, i) => (
              <div key={i} className="hover-tip" style={{ position: 'relative' }}>
                <div
                  style={{
                    borderRadius: 4,
                    padding: p.isBreak ? 2 : 0,
                    background: p.isBreak ? '#e5ad76' : 'transparent',
                  }}
                >
                  <PostIcon post={p.post} isBreak={p.isBreak} />
                </div>
                <button
                  onClick={() => {
                    markDirty()
                    const key = p.isBreak ? 'Break' : p.post
                    updateOfficer(index, {
                      posts: entry.posts.filter(
                        x => (x.isBreak || x.post === 'Break' ? 'Break' : x.post) !== key,
                      ),
                    })
                  }}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: 'var(--bg-surface)',
                    border: '1px solid #fca5a5',
                    borderRadius: '50%',
                    width: 14,
                    height: 14,
                    display: isOfficer ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 9,
                    color: '#f87171',
                    fontWeight: 700,
                    lineHeight: 1,
                    padding: 0,
                    zIndex: 5,
                  }}
                >
                  ✕
                </button>
                <div className="tip-text tip-stacked">
                  <span>{p.isBreak ? 'Break' : p.post}</span>
                  {p.times.map((t, j) => (
                    <span key={j}>{t.from}-{t.to}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CENTER: badge image + rank overlay */}
          <div
            style={{
              position: 'relative',
              width: badgeW,
              height: badgeH,
              flexShrink: 0,
              marginTop: 4,
              background: 'var(--bg-elevated)',
            }}
          >
            <img
              src={badgeImg}
              alt="Badge"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: (entry.name || isFcpdEntry) ? 1 : 0.4,
              }}
            />
            {entry.callSign && !isFcpdEntry && (
              <div
                style={{
                  position: 'absolute',
                  top: '48%',
                  left: '48%',
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 900,
                  fontSize: 34,
                  color: '#1e1b4b',
                  fontFamily: 'Arial Rounded MT Bold, Arial Black, sans-serif',
                  textAlign: 'center',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              >
                {cs}
              </div>
            )}
            {/* Transparent circular select — disabled for officers, hidden for FCPD */}
            {!isFcpdEntry && (
            <select
              value={entry.callSign}
              onChange={e => assignCS(e.target.value)}
              title={isOfficer ? 'Officers cannot reassign posts' : 'Click to assign officer'}
              disabled={isOfficer}
              style={{
                position: 'absolute',
                top: '48%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 82,
                height: 82,
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                color: 'transparent',
                cursor: isOfficer ? 'default' : 'pointer',
                padding: 0,
                zIndex: 10,
              }}
            >
              <option value="">--</option>
              {/* Show current assignment even if not in roster */}
              {entry.callSign && !rosterOptions.some(r => r.callSign === entry.callSign) && (
                <option value={entry.callSign}>
                  {entry.callSign} - {entry.name || '(assigned)'}
                </option>
              )}
              {rosterOptions.map((r, i) => (
                <option key={i} value={r.callSign}>
                  {r.callSign} - {r.name}
                </option>
              ))}
            </select>
            )}
            {/* Empty state hint — not shown for FCPD slots */}
            {!entry.callSign && !isFcpdEntry && (
              <div
                style={{
                  position: 'absolute',
                  top: '48%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  border: '2px dashed var(--border)',
                  background: 'var(--bg-deep)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  fontSize: 20,
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                }}
              >
                +
              </div>
            )}
          </div>

          {/* RIGHT: equipment icons */}
          <div
            style={{
              width: 44,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            {entry.key && (
              <div title={`Key ${entry.key}`} className="hover-tip">
                <img src={KEY_ICON} alt="Key" style={{ width: 40, height: 40, borderRadius: 4 }} />
              </div>
            )}
            {entry.radio && (
              <div title={`Radio ${entry.radio}`} className="hover-tip">
                <img src={WALKIE_ICON_BG} alt="Radio" style={{ width: 40, height: 40, borderRadius: 4 }} />
              </div>
            )}
            {entry.recorder && (
              <div title={`Recorder ${entry.recorder}`} className="hover-tip">
                <img src={REC_ICON} alt="Recorder" style={{ width: 40, height: 40, borderRadius: 4 }} />
              </div>
            )}
            {entry.flashlight && (
              <div title={`Flashlight ${entry.flashlight}`} className="hover-tip">
                <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, background: 'rgba(255,255,255,0.04)' }}>
                  <div style={{ width: 22, height: 36 }}>
                    <FlashSVG />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom: secondary tasks, name, shift, edit button */}
        {entry.name && (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            {officerTasks.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', marginBottom: 6 }}>
                {officerTasks.map((t, i) => {
                  const taskIcon = getPostIcon(t.task)
                  return (
                    <div
                      key={i}
                      className="hover-tip"
                      style={{
                        position: 'relative',
                        background: '#f2d7bb',
                        padding: 2,
                        borderRadius: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {taskIcon ? (
                        <img src={taskIcon} alt={t.label || t.task} style={{ width: 24, height: 24, borderRadius: 3 }} />
                      ) : (
                        <span style={{ fontSize: 14, width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>📌</span>
                      )}
                      <div className="tip-text tip-stacked">
                        <span>{t.label || t.task}</span>
                        {t.time && <span>{t.time}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              {stripNickname(entry.name)}
            </div>
            {(entry.shiftFrom || entry.shiftTo) && (
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginTop: 2 }}>
                🕐 {entry.shiftFrom || '??:??'} to {entry.shiftTo || '??:??'}
              </div>
            )}
            <button
              onClick={() => setShowModal(true)}
              style={{
                fontSize: 12,
                color: '#3b82f6',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginTop: 4,
              }}
            >
              Edit Equipment
            </button>
          </div>
        )}
      </div>
    </>
  )
}
